import { useState, useEffect, useMemo } from "react";
import { Outlet, NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { TourProvider, useTour } from "@/contexts/TourContext";
import { useSip } from "@/contexts/SipContext";
import SipSetupDialog from "@/components/SipSetupDialog";
import FloatingCallWidget from "@/components/FloatingCallWidget";
import TourOverlay from "@/components/TourOverlay";
import { AddContactAfterCallDialog } from "@/components/AddContactAfterCallDialog";
import ConnectionStatusIndicator from "@/components/ConnectionStatusIndicator";
import { Loader2 } from "lucide-react";
import { 
  Headset, 
  Headphones,
  History, 
  BarChart3, 
  UsersRound,
  Users,
  LogOut,
  User,
  ChevronDown,
  Building2,
  Phone,
  PhoneCall,
  PhoneOff,
  Sun,
  Moon,
  HelpCircle,
  Lock,
  Crown,
  LifeBuoy,
  FileText
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Navigation configuration - maps to backend feature IDs
const NAV_CONFIG = [
  { key: 'dialer', backendId: 'dialer', path: '/app/dialer', icon: Headset, label: 'My Phone' },
  { key: 'contacts', backendId: 'contacts', path: '/app/contacts', icon: Users, label: 'Contacts' },
  { key: 'myCalls', backendId: 'my_calls', path: '/app/my-calls', icon: PhoneCall, label: 'My Calls' },
  { key: 'queueMonitor', backendId: 'queue_monitor', path: '/app/queues', icon: UsersRound, label: 'Queue Monitor' },
  { key: 'callHistory', backendId: 'call_history', path: '/app/history', icon: History, label: 'Call History' },
  { key: 'analytics', backendId: 'analytics', path: '/app/analytics', icon: BarChart3, label: 'Analytics' },
  { key: 'reports', backendId: 'reports', path: '/app/reports', icon: FileText, label: 'Reports' },
  { key: 'companySettings', backendId: 'company_settings', path: '/app/company', icon: Building2, label: 'Company' },
];

// Route to feature mapping for permission checks
const ROUTE_FEATURE_MAP = {
  '/app/dialer': 'dialer',
  '/app/contacts': 'contacts',
  '/app/my-calls': 'my_calls',
  '/app/queues': 'queue_monitor',
  '/app/history': 'call_history',
  '/app/analytics': 'analytics',
  '/app/reports': 'reports',
  '/app/company': 'company_settings',
  '/app/profile': 'profile',
};

// Protected Outlet - checks permissions before rendering child routes
function ProtectedOutlet() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFeatureAccess, permissionsLoaded } = useAuth();
  
  // Get the required feature for the current route
  const requiredFeature = useMemo(() => {
    // Check exact match first
    if (ROUTE_FEATURE_MAP[location.pathname]) {
      return ROUTE_FEATURE_MAP[location.pathname];
    }
    // Check if any route starts with the pathname (for nested routes)
    for (const [route, feature] of Object.entries(ROUTE_FEATURE_MAP)) {
      if (location.pathname.startsWith(route)) {
        return feature;
      }
    }
    return null; // No feature restriction for this route
  }, [location.pathname]);
  
  // Check if user has access
  const hasAccess = !requiredFeature || hasFeatureAccess(requiredFeature);
  
  // Handle redirect for unauthorized access
  useEffect(() => {
    if (permissionsLoaded && !hasAccess) {
      navigate('/app/dialer', { replace: true });
    }
  }, [permissionsLoaded, hasAccess, navigate]);
  
  // Show loading while permissions are being fetched or redirecting
  if (!permissionsLoaded || !hasAccess) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return <Outlet />;
}

export default function Layout() {
  return (
    <ThemeProvider>
      <TourProvider>
        <LayoutContent />
      </TourProvider>
    </ThemeProvider>
  );
}

function LayoutContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    user, 
    logout, 
    isDeveloper, 
    isCustomerAdmin, 
    isPhoneAdmin, 
    needsSipSetup, 
    hasFeatureAccess, 
    userFeatures,
    isFeatureRestrictedByPlan,
    getFeatureRestrictionReason,
    getCurrentPlan
  } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { startTour } = useTour();
  const { sipStatus } = useSip();
  const [showSipSetup, setShowSipSetup] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = "Connect | Cloud PBX Console";
  }, []);

  // Show SIP setup dialog if user needs it (on first load)
  useEffect(() => {
    if (needsSipSetup()) {
      setShowSipSetup(true);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build navigation items from NAV_CONFIG, including restricted ones
  const navItems = NAV_CONFIG.map(item => {
    const hasAccess = hasFeatureAccess(item.backendId);
    const isRestricted = isFeatureRestrictedByPlan(item.backendId);
    const restrictionReason = getFeatureRestrictionReason(item.backendId);
    const isDialerDisabled = item.key === 'dialer' && user?.dialer_enabled === false;
    
    return {
      ...item,
      path: item.path,
      icon: item.icon,
      label: item.label,
      testId: `nav-${item.key.replace(/([A-Z])/g, '-$1').toLowerCase()}`,
      hasAccess,
      isRestricted,
      restrictionReason,
      isDialerDisabled
    };
  }).filter(item => item.hasAccess || item.isRestricted); // Show accessible OR plan-restricted (not role-restricted)

  // Role display badge
  const getRoleBadge = () => {
    const roleMap = {
      developer: { label: 'Developer', variant: 'destructive' },
      customer_admin: { label: 'Admin', variant: 'default' },
      phone_admin: { label: 'Phone Admin', variant: 'secondary' },
      standard: { label: 'User', variant: 'outline' }
    };
    const role = roleMap[user?.role] || roleMap.standard;
    return <Badge variant={role.variant} className="text-xs">{role.label}</Badge>;
  };
  
  // Plan display badge
  const getPlanBadge = () => {
    const plan = getCurrentPlan();
    const planMap = {
      free: { label: 'Free', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
      pro: { label: 'Pro', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
    };
    const planInfo = planMap[plan] || planMap.free;
    return (
      <Badge variant="outline" className={`text-xs ${planInfo.className}`}>
        {planInfo.label}
      </Badge>
    );
  };

  return (
    <div className="flex h-screen bg-background" data-testid="app-layout">
      {/* Sidebar - Fixed height with user at bottom */}
      <aside className={`w-64 border-r border-border/50 flex flex-col h-screen fixed left-0 top-0 ${isDark ? 'bg-[#0a0a0b]' : 'bg-slate-50'}`} data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-border/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src="/connect-logo.png" alt="CONNECT" className="w-10 h-10 rounded-lg" />
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight text-foreground">
                CONNECT
              </h1>
              <p className="text-xs text-muted-foreground">Cloud PBX Console</p>
            </div>
          </div>
        </div>

        {/* Navigation - Scrollable middle section */}
        <nav className="flex-1 p-4 overflow-y-auto min-h-0">
          <TooltipProvider>
            <ul className="space-y-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const isLocked = item.isRestricted;
                
                if (isLocked) {
                  // Render locked item with tooltip
                  return (
                    <li key={item.path}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => navigate('/app/plan')}
                            data-testid={item.testId}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 text-muted-foreground/50 hover:text-muted-foreground cursor-pointer group"
                          >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium flex-1 text-left">{item.label}</span>
                            <Lock className="w-4 h-4 text-amber-500/70 group-hover:text-amber-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[200px]">
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4 text-amber-500" />
                            <span className="text-sm">Upgrade to unlock</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.restrictionReason || 'This feature requires a paid plan'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                }

                // Dialer disabled (DND mode) — grey out with tooltip
                if (item.isDialerDisabled) {
                  return (
                    <li key={item.path}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => navigate('/app/profile')}
                            data-testid={item.testId}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 text-muted-foreground/40 hover:text-muted-foreground/60 cursor-pointer"
                          >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium flex-1 text-left">{item.label}</span>
                            <PhoneOff className="w-4 h-4 text-red-400/60" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[220px]">
                          <p className="text-sm font-medium">Dialer is turned off</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Go to My Settings to turn it back on
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                }
                
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      data-testid={item.testId}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                        isActive
                          ? "bg-primary/10 text-primary border border-primary/30"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </TooltipProvider>
        </nav>

        {/* User section - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-border/50">
          <div className="p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-between h-auto py-3 px-3" data-testid="user-menu-trigger">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
                      {user?.profile_image ? (
                        <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium truncate max-w-[100px]">
                        {user?.name || user?.phone_number || 'User'}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {getRoleBadge()}
                        {getPlanBadge()}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name || 'User'}</span>
                    <div className="flex items-center gap-2 mt-1">
                      <Phone className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground font-mono">{user?.phone_number}</span>
                      <div className={`w-2 h-2 rounded-full ${sipStatus === 'registered' ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                    </div>
                    {user?.company_name && (
                      <span className="text-xs text-muted-foreground mt-1">{user?.company_name}</span>
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      {getPlanBadge()}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isCustomerAdmin() && (
                  <DropdownMenuItem onClick={() => navigate('/app/plan')} className="cursor-pointer" data-testid="change-plan-btn">
                    <Crown className="w-4 h-4 mr-2 text-amber-500" />
                    Change Plan
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/app/profile')} className="cursor-pointer" data-testid="profile-menu-btn">
                  <User className="w-4 h-4 mr-2" />
                  My Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/support')} className="cursor-pointer" data-testid="support-menu-btn">
                  <LifeBuoy className="w-4 h-4 mr-2" />
                  Support
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer" data-testid="theme-toggle-btn">
                  {isDark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={startTour} className="cursor-pointer" data-testid="start-tour-btn">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Take a Tour
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Footer Links */}
          <div className="px-4 pb-4 space-y-2">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <Link to="/app/about" className="hover:text-foreground transition-colors">About</Link>
              <span>•</span>
              <Link to="/app/help" className="hover:text-foreground transition-colors">Help</Link>
              <span>•</span>
              <Link to="/app/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <span>•</span>
              <Link to="/app/terms" className="hover:text-foreground transition-colors">Terms</Link>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Connect-US Cloud PBX
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content - Offset by sidebar width */}
      <main className="flex-1 overflow-auto ml-64 bg-background">
        <ProtectedOutlet />
      </main>

      {/* Connection Status Indicator - top right */}
      <ConnectionStatusIndicator />

      {/* Floating Call Widget - shows on all pages except dialer when in a call */}
      <FloatingCallWidget />

      {/* Tour Overlay */}
      <TourOverlay />
      
      {/* Add Contact After Call Dialog */}
      <AddContactAfterCallDialog />

      {/* SIP Setup Dialog for imported users */}
      <SipSetupDialog 
        open={showSipSetup} 
        onClose={() => setShowSipSetup(false)}
      />
    </div>
  );
}
